import React, { useEffect, useState } from "react";
import { Search, Filter, Plus, Pencil, Power } from "lucide-react";
import { useNavigate } from "react-router-dom";

import DashboardLayout from "src/components/layout/dashboard/Layout";
import TableV2 from "src/components/ui/TableV2";
import { Badge } from "src/components/ui/Badge";
import PageHeader from "src/components/common/PageHeader";
import CardHeader from "src/components/common/card/CardHeader";
import Button from "src/components/ui/Button";
import Card from "src/components/common/card/Card";
import ConfirmationModal from "src/components/modals/ConfirmationModal";

import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import * as productActions from "src/redux/slices/productSlice";
import { formatDateTime } from "src/utils/date-time";
import { Product } from "src/types/product.types";
import { toast } from "react-toastify";
import {
  currencyFormatter,
  formatCreatedBy,
  truncateText,
} from "src/utils/common";

const ProductListPage: React.FC = () => {
  const [limit] = useState(10);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [statusRow, setStatusRow] = useState<Product | null>(null);
  const [statusUpdate, setStatusUpdate] = useState(false);

  const { productList, editProduct } = useAppSelector((state) => state.product);
  const {
    user: { data: user },
  } = useAppSelector((state) => state.authTenant);
  const permissions =
    user?.roleDetails?.permissions || user?.permissions || {};
  const isTenantAdmin =
    user?.role === "admin" ||
    user?.roleDetails?.name?.toLowerCase() === "admin";
  const canCreateProduct =
    isTenantAdmin || (permissions?.products || []).includes("create");
  const canEditProduct =
    isTenantAdmin || (permissions?.products || []).includes("edit");
  const canToggleProduct =
    isTenantAdmin || (permissions?.products || []).includes("delete");
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    getProductList(1);
  }, []);

  // ✅ Success/Error side-effects
  useEffect(() => {
    if (editProduct.data && statusUpdate) {
      toast.success("Product modified successfully");
      dispatch(productActions.resetEditProduct());
      setStatusUpdate(false);
      handleStatusModalClose();
      getProductList(1);
    } else if (editProduct.error && statusUpdate) {
      toast.error(`Error: ${editProduct.error}`);
      setStatusUpdate(false);
      handleStatusModalClose();
    }
  }, [editProduct, dispatch, statusUpdate]);

  const getProductList = (page: number) =>
    dispatch(
      productActions.getProductListRequest({
        page,
        limit,
      })
    );

  const handlePageChange = (page: number) => {
    getProductList(page);
  };

  const handleStatusUpdate = (_id: string, status: "active" | "inactive") => {
    const payload = {
      _id: _id,
      status: status,
    };
    dispatch(productActions.editProductRequest(payload));
  };

  const handleStatusModalClose = () => {
    setShowStatusModal(false);
    setStatusRow(null);
  };

  const onConfirmStatus = () => {
    if (!statusRow?._id) return;
    setStatusUpdate(true);
    handleStatusUpdate(
      statusRow._id,
      statusRow.status === "active" ? "inactive" : "active"
    );
  };

  const handleEdit = (data: Product) => {
    navigate(`/app/product/${data._id}/edit`, { state: { product: data } });
  };

  return (
    <DashboardLayout
      meta={{
        title: "Products | Invyntra",
        description:
          "Track and manage all product plans with detailed customer and pricing information.",
        keywords: "plans, products, invoices, inventory, invyntra",
      }}
    >
      <div className="space-y-4">
        {/* Header */}
        <PageHeader
          title="Products"
          breadcrumbData={[
            { label: "Home", path: "/app/dashboard" },
            { label: "Products", path: "/app/product/list" },
          ]}
        />

        <Card>
          <CardHeader
            title="Products"
            description="List of Products"
            actions={
              <div className="flex items-center justify-stretch flex-wrap gap-3">
                <Button variant="outline">
                  <Search size={16} className="mr-1" />
                  <span>Search</span>
                </Button>

                <Button variant="outline">
                  <Filter size={16} className="mr-1" />
                  <span>Filter</span>
                </Button>

                {canCreateProduct && (
                  <Button onClick={() => navigate("/app/product/create")}>
                    <Plus size={16} className="mr-1" />
                    <span>Add Products</span>
                  </Button>
                )}
              </div>
            }
          />

          {/* Table */}
          <TableV2
            columns={[
              {
                key: "productId",
                header: "Product ID",
                accessor: ({ key, row }) => (
                  <div className="w-20">{row[key]}</div>
                ),
              },
              {
                key: "name",
                header: "Product Name",
                width: "200px",
                accessor: ({ key, row }) => (
                  <div className="font-medium">
                    <div>{row[key]}</div>
                    {row?.sku && (
                      <p className="text-xs text-gray-500">
                        <b>SKU:</b> {row?.sku}
                      </p>
                    )}
                  </div>
                ),
              },
              {
                key: "description",
                header: "Description",
                width: "150px",
                accessor: ({ key, row }) => (
                  <div>{truncateText(row[key], 50)}</div>
                ),
              },
              {
                key: "category",
                header: "Category",
                accessor: ({ key, row }) => (
                  <div>
                    <div>{row?.categoryId?.name}</div>
                    {row?.subCategoryId && (
                      <div>/ {row?.subCategoryId?.name}</div>
                    )}
                  </div>
                ),
              },
              {
                key: "mrp",
                header: "MRP",
                align: "right",
                accessor: ({ key, row }) => (
                  <div>{currencyFormatter(row[key])}</div>
                ),
              },
              {
                key: "sellingPrice",
                header: "Selling Price",
                align: "right",
                accessor: ({ key, row }) => (
                  <div>{currencyFormatter(row[key])}</div>
                ),
              },
              { key: "minStock", header: "Min Stock" },
              { key: "stock", header: "Available Stock" },
              {
                key: "createdBy",
                header: "Created By",
                accessor: ({ row }) => formatCreatedBy(row?.createdBy),
              },
              {
                key: "createdAt",
                header: "Published On",
                accessor: ({ key, row }) => (
                  <div>{formatDateTime(row[key], "DD/MM/YYYY")}</div>
                ),
              },
              {
                key: "status",
                header: "Status",
                accessor: ({ key, row }) => (
                  <Badge
                    text={row?.[key] || ""}
                    color={row?.[key] === "active" ? "green" : "red"}
                  />
                ),
              },
              {
                key: "actions",
                header: "Actions",
                accessor: ({ row }) => (
                  <div className="pr-2">
                    {canEditProduct && (
                      <button
                        className="p-2 rounded-md hover:bg-primary-100 text-primary-600"
                        onClick={() => handleEdit(row)}
                      >
                        <Pencil size={14} />
                      </button>
                    )}
                    {canToggleProduct && (
                      <button
                        className={`p-2 rounded-md ${
                          row.status === "active"
                            ? "hover:bg-red-100 text-red-600"
                            : "hover:bg-green-100 text-green-600"
                        }`}
                        onClick={() => {
                          setStatusRow(row);
                          setShowStatusModal(true);
                        }}
                      >
                        <Power size={14} />
                      </button>
                    )}
                  </div>
                ),
              },
            ]}
            data={productList?.data?.data}
            pagination={productList?.data?.pagination}
            onPageChange={handlePageChange}
          />
        </Card>
      </div>

      {showStatusModal && statusRow?._id && (
        <ConfirmationModal
          message={`Are you sure to ${
            statusRow.status === "active" ? "deactivate" : "activate"
          } this product?`}
          onConfirm={onConfirmStatus}
          onCancel={handleStatusModalClose}
          open={showStatusModal}
          isLoading={editProduct.loading}
        />
      )}
    </DashboardLayout>
  );
};

export default ProductListPage;
